<script setup lang="ts">
import type { IconName } from '~ds/lib/icons';

import { Icon } from '~ds/components/ui/icon';
import { Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import KUICoreClickable from '~kui/components/KUI/Core/Clickable/KUICoreClickable.vue';

interface Props {
  title: string;
  description?: string;
  icon?: IconName;
  ariaLabel?: string;
  /**
   * Interactive alert: renders as a KUICoreClickable (button) with a trailing arrow and emits
   * `click`. When false the alert is static — a plain div wrapper with no arrow.
   */
  isClickable?: boolean;
}

withDefaults(defineProps<Props>(), {
  description: '',
  icon: ICONS.userAlert,
  ariaLabel: undefined,
  isClickable: true,
});

defineEmits<{ click: [e: MouseEvent] }>();
</script>

<template>
  <component
    :is="isClickable ? KUICoreClickable : 'div'"
    :aria-label="isClickable ? ariaLabel : undefined"
    class="flex w-full items-center gap-4 rounded-2xl bg-brand-orange-subtle p-4 text-left group"
    @click="isClickable ? $emit('click', $event) : undefined"
  >
    <div class="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted/50 text-foreground">
      <Icon :name="icon" size="lg" />
    </div>
    <div class="flex min-w-0 flex-1 flex-col gap-2">
      <Text
        size="lg"
        weight="semibold"
        class="text-foreground leading-none"
      >
        {{ title }}
      </Text>
      <Text
        v-if="description"
        size="sm"
        class="text-muted-foreground"
      >
        {{ description }}
      </Text>
    </div>
    <Icon
      v-if="isClickable"
      :name="ICONS.arrowRightSolid"
      size="lg"
      class="group-hover:translate-x-0.5 group-hover:scale-110 transition-transform duration-300 shrink-0 text-foreground leading-none md:size-4"
    />
  </component>
</template>
